import { useVirtualizer } from '@tanstack/react-virtual'
import {
    Check,
    ChevronDown,
    Search,
    Sparkles,
    TrendingDown,
    TrendingUp,
    X,
    Zap,
} from 'lucide-react'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/utils'

export interface MaterialSelectOption {
    value: string
    label: string | React.ReactNode
    subtitle?: string
    badge?: string
    badgeColor?: 'blue' | 'purple' | 'emerald' | 'amber' | 'indigo' | 'rose' | 'slate'
    tag?: string
    category?: string
    isPinned?: boolean
    icon?: React.ReactNode
    ltp?: number
    changePercent?: number
}

export interface MaterialSelectProps {
    value: string
    onChange: (value: string) => void
    options: MaterialSelectOption[]
    className?: string
    disabled?: boolean
    variant?: 'filled' | 'text' | 'custom'
    align?: 'start' | 'center' | 'end' | 'left' | 'right'
    menuClassName?: string
    placeholder?: string
    headerTitle?: string
    searchable?: boolean
    searchPlaceholder?: string
    pinnedValues?: string[]
    icon?: React.ReactNode
    showTagInTrigger?: boolean
    showQuickToggle?: boolean
    getLiveQuote?: (value: string) => { ltp?: number; changePercent?: number; strikeGap?: number } | undefined
}

const getBadgeClasses = (color?: string) => {
    switch (color) {
        case 'blue':
            return 'bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 border border-blue-200/60 dark:border-blue-800/60'
        case 'purple':
            return 'bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 border border-purple-200/60 dark:border-purple-800/60'
        case 'emerald':
            return 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-200/60 dark:border-emerald-800/60'
        case 'amber':
            return 'bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border border-amber-200/60 dark:border-amber-800/60'
        case 'rose':
            return 'bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 border border-rose-200/60 dark:border-rose-800/60'
        case 'slate':
            return 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200/60 dark:border-slate-700/60'
        case 'indigo':
        default:
            return 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-800/60'
    }
}

export const MaterialSelect: React.FC<MaterialSelectProps> = ({
    value,
    onChange,
    options,
    className = '',
    disabled = false,
    variant = 'filled',
    align = 'start',
    menuClassName = '',
    placeholder = 'Select...',
    headerTitle,
    searchable,
    searchPlaceholder = 'Search...',
    pinnedValues,
    icon,
    showTagInTrigger = false,
    showQuickToggle = false,
    getLiveQuote,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [optimisticValue, setOptimisticValue] = useState(value)
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedCategory, setSelectedCategory] = useState<string>('ALL')
    const [highlightedIndex, setHighlightedIndex] = useState(-1)

    const triggerRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const listParentRef = useRef<HTMLDivElement>(null)
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})

    // Synchronize optimistic value when parent value changes
    useEffect(() => {
        setOptimisticValue(value)
    }, [value])

    const isSearchEnabled = searchable !== undefined ? searchable : options.length > 5

    // Extract unique categories/badges for quick-filter tabs
    const categories = useMemo(() => {
        const set = new Set<string>()
        for (const o of options) {
            const cat = o.category || o.badge
            if (cat) set.add(cat)
        }
        return Array.from(set)
    }, [options])

    // Filter options based on search query, category, and pinned ordering
    const filteredOptions = useMemo(() => {
        let list = options

        // 1. Filter by category if selected
        if (selectedCategory !== 'ALL') {
            list = list.filter((o) => (o.category || o.badge) === selectedCategory)
        }

        // 2. Filter by search query (instant prefix / substring match)
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim()
            list = list.filter((o) => {
                const valMatch = String(o.value).toLowerCase().includes(q)
                const labelMatch =
                    typeof o.label === 'string' && o.label.toLowerCase().includes(q)
                const subMatch = o.subtitle ? o.subtitle.toLowerCase().includes(q) : false
                const badgeMatch = o.badge ? o.badge.toLowerCase().includes(q) : false
                const tagMatch = o.tag ? o.tag.toLowerCase().includes(q) : false
                return valMatch || labelMatch || subMatch || badgeMatch || tagMatch
            })
        }

        // 3. If pinnedValues provided, prioritize them when not actively searching
        if (pinnedValues && pinnedValues.length > 0 && !searchQuery.trim()) {
            const pinnedSet = new Set(pinnedValues)
            const pinnedList: MaterialSelectOption[] = []
            const unpinnedList: MaterialSelectOption[] = []

            for (const o of list) {
                if (pinnedSet.has(o.value) || o.isPinned) {
                    pinnedList.push(o)
                } else {
                    unpinnedList.push(o)
                }
            }
            return [...pinnedList, ...unpinnedList]
        }

        return list
    }, [options, searchQuery, selectedCategory, pinnedValues])

    const alignProp = align === 'left' ? 'start' : align === 'right' ? 'end' : align

    // High performance position calculation with zero layout lag
    const updatePosition = useCallback(() => {
        if (!triggerRef.current) return
        const rect = triggerRef.current.getBoundingClientRect()
        const menuWidth = Math.max(rect.width, isSearchEnabled ? 300 : 250)
        const spaceBelow = window.innerHeight - rect.bottom
        const approxHeight = Math.min(filteredOptions.length * 52 + 120, 420)

        let left = rect.left
        if (alignProp === 'end') {
            left = rect.right - menuWidth
        } else if (alignProp === 'center') {
            left = rect.left + (rect.width - menuWidth) / 2
        }

        // Horizontal screen bounds check
        if (left + menuWidth > window.innerWidth - 8) {
            left = window.innerWidth - menuWidth - 8
        }
        if (left < 8) {
            left = 8
        }

        // Vertical screen bounds check
        let top = rect.bottom + 6
        if (spaceBelow < approxHeight && rect.top > approxHeight) {
            top = Math.max(8, rect.top - approxHeight - 6)
        }

        setMenuStyle({
            position: 'fixed',
            top: `${top}px`,
            left: `${left}px`,
            width: `${menuWidth}px`,
            maxWidth: '380px',
            zIndex: 99999,
        })
    }, [alignProp, filteredOptions.length, isSearchEnabled])

    // Ultra-responsive toggle — triggers immediately on pointerdown (0ms physical delay)
    const handleToggle = useCallback(
        (e: React.SyntheticEvent) => {
            e.preventDefault()
            e.stopPropagation()
            if (disabled) return
            if (!isOpen) {
                updatePosition()
                setSearchQuery('')
                setSelectedCategory('ALL')
                const currentIndex = filteredOptions.findIndex(
                    (o) => o.value === optimisticValue || o.value === value,
                )
                setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0)
                // Schedule instant search input focus on opening
                requestAnimationFrame(() => searchInputRef.current?.focus())
            }
            setIsOpen((prev) => !prev)
        },
        [disabled, isOpen, updatePosition, filteredOptions, optimisticValue, value],
    )

    // Decoupled instant selection handler:
    // Frame 0: Dismisses popover and updates local trigger button in < 1ms
    // Frame 1: Dispatches store change in background macrotask, preventing any UI freeze
    const handleSelect = useCallback(
        (val: string, e?: React.SyntheticEvent) => {
            if (e) {
                e.preventDefault()
                e.stopPropagation()
            }
            // 1. Instant optimistic visual feedback (0ms)
            setOptimisticValue(val)
            setIsOpen(false)

            // 2. Yield control so browser commits Frame 0 dismissal before heavy table re-renders
            requestAnimationFrame(() => {
                setTimeout(() => {
                    onChange(val)
                }, 0)
            })
        },
        [onChange],
    )

    // Virtualizer for scalable 100+ items list
    const isVirtual = filteredOptions.length > 12
    const virtualizer = useVirtualizer({
        count: filteredOptions.length,
        getScrollElement: () => listParentRef.current,
        estimateSize: () => 52,
        overscan: 5,
        enabled: isVirtual && isOpen,
    })

    // Click outside, keyboard, and viewport listeners
    useEffect(() => {
        if (!isOpen) return

        updatePosition()

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node
            if (
                triggerRef.current &&
                !triggerRef.current.contains(target) &&
                menuRef.current &&
                !menuRef.current.contains(target)
            ) {
                setIsOpen(false)
            }
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                setIsOpen(false)
                triggerRef.current?.focus()
            } else if (event.key === 'ArrowDown') {
                event.preventDefault()
                setHighlightedIndex((prev) => {
                    const next = prev < filteredOptions.length - 1 ? prev + 1 : 0
                    if (isVirtual) {
                        virtualizer.scrollToIndex(next, { align: 'auto' })
                    }
                    return next
                })
            } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                setHighlightedIndex((prev) => {
                    const next = prev > 0 ? prev - 1 : filteredOptions.length - 1
                    if (isVirtual) {
                        virtualizer.scrollToIndex(next, { align: 'auto' })
                    }
                    return next
                })
            } else if (event.key === 'Enter') {
                event.preventDefault()
                if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                    handleSelect(filteredOptions[highlightedIndex]!.value)
                } else if (filteredOptions.length > 0 && filteredOptions[0]) {
                    handleSelect(filteredOptions[0].value)
                }
            } else if (event.key === 'Home') {
                event.preventDefault()
                setHighlightedIndex(0)
                if (isVirtual) virtualizer.scrollToIndex(0, { align: 'start' })
            } else if (event.key === 'End') {
                event.preventDefault()
                const last = filteredOptions.length - 1
                setHighlightedIndex(last)
                if (isVirtual) virtualizer.scrollToIndex(last, { align: 'end' })
            }
        }

        const handleScrollOrResize = () => {
            updatePosition()
        }

        document.addEventListener('mousedown', handleClickOutside, true)
        document.addEventListener('keydown', handleKeyDown)
        window.addEventListener('scroll', handleScrollOrResize, true)
        window.addEventListener('resize', handleScrollOrResize)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true)
            document.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('scroll', handleScrollOrResize, true)
            window.removeEventListener('resize', handleScrollOrResize)
        }
    }, [isOpen, updatePosition, filteredOptions, highlightedIndex, handleSelect, isVirtual, virtualizer])

    // Find option matching current value
    const selectedOption =
        options.find((o) => o.value === optimisticValue) ||
        options.find((o) => o.value === value)

    const displayLabel = selectedOption ? selectedOption.label : optimisticValue || placeholder

    // Quick toggle suitability (e.g. 2 expiry cycles: Current & Next)
    const shouldShowQuickToggle =
        (showQuickToggle || (options.length === 2 && !isSearchEnabled)) &&
        !searchQuery.trim()

    return (
        <div className="relative inline-block shrink-0">
            {/* Pill Trigger */}
            <button
                type="button"
                ref={triggerRef}
                onPointerDown={handleToggle}
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                }}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                className={cn(
                    'select-none transition-all outline-none cursor-pointer',
                    isOpen &&
                        'ring-2 ring-indigo-500/40 border-indigo-400 dark:border-indigo-400/90 shadow-md shadow-indigo-500/10',
                    variant !== 'custom' &&
                        'h-auto min-h-[32px] gap-2 px-3 py-2 rounded-xl text-xs font-bold border-none focus:ring-0',
                    variant === 'filled' &&
                        'bg-white/40 dark:bg-slate-800/40 hover:bg-white/60 dark:hover:bg-slate-800/60 backdrop-blur-md border border-white/40 dark:border-slate-700/40 shadow-sm text-slate-700 dark:text-slate-200 hover:shadow-md hover:border-indigo-200 dark:hover:border-indigo-500/50 ring-1 ring-white/40 dark:ring-white/10',
                    variant === 'text' &&
                        'bg-transparent hover:bg-slate-100/50 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 p-0',
                    disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
                    className,
                )}
            >
                <div className="flex items-center justify-between w-full gap-2 outline-none">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        {icon && <span className="shrink-0">{icon}</span>}
                        {selectedOption?.badge && (
                            <span
                                className={cn(
                                    'text-[9px] px-1.5 py-0.5 rounded-full font-black tracking-wider uppercase shrink-0 transition-colors',
                                    getBadgeClasses(selectedOption.badgeColor),
                                )}
                            >
                                {selectedOption.badge}
                            </span>
                        )}
                        <span className="truncate">{displayLabel}</span>
                        {showTagInTrigger && selectedOption?.tag && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-black font-mono tracking-tight tabular-nums bg-slate-100 dark:bg-slate-700/80 text-slate-600 dark:text-slate-300 shrink-0">
                                {selectedOption.tag}
                            </span>
                        )}
                    </div>
                    <ChevronDown
                        size={14}
                        className={cn(
                            'opacity-50 shrink-0 transition-transform duration-200',
                            isOpen && 'rotate-180 opacity-100 text-indigo-500',
                        )}
                    />
                </div>
            </button>

            {/* Fast, lightweight, hardware-accelerated portal dropdown */}
            {isOpen &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        ref={menuRef}
                        style={menuStyle}
                        className={cn(
                            'material-select-portal bg-white/95 dark:bg-[#0c1322]/95 backdrop-blur-3xl rounded-2xl shadow-[0_20px_50px_-10px_rgba(15,23,42,0.35)] dark:shadow-[0_20px_50px_-10px_rgba(0,0,0,0.8)] border border-slate-200/90 dark:border-slate-800/90 ring-1 ring-black/5 dark:ring-white/10 overflow-hidden animate-in fade-in zoom-in-95 duration-100 p-2 select-none flex flex-col',
                            menuClassName,
                        )}
                    >
                        {/* Header Title */}
                        {headerTitle && (
                            <div className="flex items-center justify-between px-2 pt-1 pb-1.5 text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 border-b border-slate-100 dark:border-slate-800/60 mb-1.5">
                                <span className="flex items-center gap-1.5">
                                    <Sparkles size={10} className="text-indigo-500" />
                                    {headerTitle}
                                </span>
                                <span className="font-mono text-[10px] text-indigo-600 dark:text-indigo-400 font-bold">
                                    {filteredOptions.length}
                                </span>
                            </div>
                        )}

                        {/* Quick 1-Tap Segmented Cards (e.g. Current vs Next Expiry) */}
                        {shouldShowQuickToggle && (
                            <div className="grid grid-cols-2 gap-1.5 mb-2 p-1 bg-slate-100/70 dark:bg-slate-800/50 rounded-xl border border-slate-200/60 dark:border-slate-800">
                                {options.slice(0, 2).map((opt, idx) => {
                                    const isOptSelected =
                                        opt.value === optimisticValue || opt.value === value
                                    const isCurrent = idx === 0
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onPointerDown={(e) => handleSelect(opt.value, e)}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                            }}
                                            className={cn(
                                                'flex flex-col items-start p-2 rounded-lg text-left transition-all duration-100 cursor-pointer outline-none relative select-none',
                                                isOptSelected
                                                    ? 'bg-white dark:bg-slate-700 shadow-md ring-2 ring-indigo-500 text-indigo-900 dark:text-white'
                                                    : 'hover:bg-white/60 dark:hover:bg-slate-700/60 text-slate-600 dark:text-slate-300',
                                            )}
                                        >
                                            <div className="flex items-center justify-between w-full mb-1">
                                                <span
                                                    className={cn(
                                                        'text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md flex items-center gap-1',
                                                        isCurrent
                                                            ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30'
                                                            : 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-500/30',
                                                    )}
                                                >
                                                    {isCurrent ? (
                                                        <Zap size={9} className="fill-current" />
                                                    ) : (
                                                        <Sparkles size={9} />
                                                    )}
                                                    {opt.badge || (isCurrent ? 'Current' : 'Next')}
                                                </span>
                                                {opt.tag && (
                                                    <span className="text-[10px] font-black font-mono tabular-nums text-slate-500 dark:text-slate-400">
                                                        {opt.tag}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-xs font-black tracking-tight truncate w-full font-mono">
                                                {opt.value}
                                            </span>
                                            <span className="text-[10px] text-slate-400 dark:text-slate-500 truncate w-full">
                                                {opt.subtitle ||
                                                    (isCurrent ? 'Active Cycle' : 'Next Cycle')}
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>
                        )}

                        {/* Search Bar (Enabled when searchable or options > 5) */}
                        {isSearchEnabled && (
                            <div className="relative mb-1.5 px-0.5">
                                <Search
                                    size={13}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none"
                                />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value)
                                        setHighlightedIndex(0)
                                    }}
                                    placeholder={searchPlaceholder}
                                    className="w-full bg-slate-100/80 dark:bg-slate-800/70 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 rounded-xl pl-8 pr-7 py-1.5 text-xs font-bold outline-none border border-transparent focus:border-indigo-500/50 focus:bg-white dark:focus:bg-slate-800 transition-all"
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5 cursor-pointer"
                                    >
                                        <X size={12} />
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Category Quick Filter Pills (Optional) */}
                        {categories.length > 1 && (
                            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar pb-1.5 mb-1 px-0.5">
                                <button
                                    type="button"
                                    onClick={() => setSelectedCategory('ALL')}
                                    className={cn(
                                        'px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all cursor-pointer',
                                        selectedCategory === 'ALL'
                                            ? 'bg-indigo-600 text-white shadow-sm'
                                            : 'bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700/60',
                                    )}
                                >
                                    All ({options.length})
                                </button>
                                {categories.map((cat) => {
                                    const count = options.filter(
                                        (o) => (o.category || o.badge) === cat,
                                    ).length
                                    return (
                                        <button
                                            key={cat}
                                            type="button"
                                            onClick={() => setSelectedCategory(cat)}
                                            className={cn(
                                                'px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all cursor-pointer',
                                                selectedCategory === cat
                                                    ? 'bg-indigo-600 text-white shadow-sm'
                                                    : 'bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700/60',
                                            )}
                                        >
                                            {cat} ({count})
                                        </button>
                                    )
                                })}
                            </div>
                        )}

                        {/* Options List Container */}
                        <div
                            ref={listParentRef}
                            className="max-h-64 overflow-y-auto custom-scrollbar"
                            style={{ position: 'relative' }}
                        >
                            {filteredOptions.length === 0 ? (
                                <div className="py-6 text-center text-xs text-slate-400 dark:text-slate-500 font-medium">
                                    No items found
                                </div>
                            ) : isVirtual ? (
                                /* Virtualized Row Rendering (Zero DOM bloat for 100+ items) */
                                <div
                                    style={{
                                        height: `${virtualizer.getTotalSize()}px`,
                                        width: '100%',
                                        position: 'relative',
                                    }}
                                >
                                    {virtualizer.getVirtualItems().map((virtualRow) => {
                                        const option = filteredOptions[virtualRow.index]
                                        if (!option) return null
                                        const isSelected =
                                            option.value === optimisticValue ||
                                            option.value === value
                                        const isHighlighted =
                                            virtualRow.index === highlightedIndex
                                        const liveQuote = getLiveQuote?.(option.value)

                                        return (
                                            <div
                                                key={option.value}
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: `${virtualRow.size}px`,
                                                    transform: `translateY(${virtualRow.start}px)`,
                                                }}
                                                className="pb-1"
                                            >
                                                <button
                                                    type="button"
                                                    onPointerDown={(e) =>
                                                        handleSelect(option.value, e)
                                                    }
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        e.stopPropagation()
                                                    }}
                                                    onMouseEnter={() =>
                                                        setHighlightedIndex(virtualRow.index)
                                                    }
                                                    className={cn(
                                                        'w-full h-full flex items-center justify-between px-3 rounded-xl cursor-pointer transition-all duration-75 text-left outline-none group/item',
                                                        isSelected
                                                            ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-md shadow-indigo-500/25'
                                                            : isHighlighted
                                                              ? 'bg-slate-100 dark:bg-slate-800/80 text-indigo-600 dark:text-indigo-300'
                                                              : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100/80 dark:hover:bg-slate-800/60',
                                                    )}
                                                >
                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                        {option.badge && (
                                                            <span
                                                                className={cn(
                                                                    'text-[9px] px-1.5 py-0.5 rounded-md font-black uppercase tracking-wider shrink-0 transition-colors',
                                                                    isSelected
                                                                        ? 'bg-white/20 text-white border border-white/30'
                                                                        : getBadgeClasses(
                                                                              option.badgeColor,
                                                                          ),
                                                                )}
                                                            >
                                                                {option.badge}
                                                            </span>
                                                        )}
                                                        <div className="flex flex-col min-w-0">
                                                            <span
                                                                className={cn(
                                                                    'text-xs font-black tracking-tight truncate',
                                                                    isSelected
                                                                        ? 'text-white'
                                                                        : 'text-slate-800 dark:text-slate-100',
                                                                )}
                                                            >
                                                                {option.label}
                                                            </span>
                                                            {option.subtitle && (
                                                                <span
                                                                    className={cn(
                                                                        'text-[10px] font-bold leading-tight truncate',
                                                                        isSelected
                                                                            ? 'text-indigo-100/80'
                                                                            : 'text-slate-400 dark:text-slate-500',
                                                                    )}
                                                                >
                                                                    {option.subtitle}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2 shrink-0 ml-3">
                                                        {liveQuote && liveQuote.ltp ? (
                                                            <div className="flex flex-col items-end">
                                                                <span
                                                                    className={cn(
                                                                        'text-xs font-black font-mono tracking-tight tabular-nums',
                                                                        isSelected
                                                                            ? 'text-white'
                                                                            : 'text-slate-800 dark:text-slate-100',
                                                                    )}
                                                                >
                                                                    ₹
                                                                    {liveQuote.ltp.toLocaleString(
                                                                        'en-IN',
                                                                        {
                                                                            minimumFractionDigits: 2,
                                                                            maximumFractionDigits: 2,
                                                                        },
                                                                    )}
                                                                </span>
                                                                {liveQuote.changePercent !==
                                                                    undefined && (
                                                                    <span
                                                                        className={cn(
                                                                            'text-[9px] font-bold font-mono tracking-tight tabular-nums flex items-center gap-0.5',
                                                                            isSelected
                                                                                ? 'text-white/90'
                                                                                : liveQuote.changePercent >=
                                                                                    0
                                                                                  ? 'text-emerald-600 dark:text-emerald-400'
                                                                                  : 'text-rose-600 dark:text-rose-400',
                                                                        )}
                                                                    >
                                                                        {liveQuote.changePercent >= 0
                                                                            ? '+'
                                                                            : ''}
                                                                        {liveQuote.changePercent.toFixed(
                                                                            2,
                                                                        )}
                                                                        %
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ) : option.tag ? (
                                                            <span
                                                                className={cn(
                                                                    'text-[10px] px-2 py-0.5 rounded-full font-bold font-mono tracking-tight tabular-nums',
                                                                    isSelected
                                                                        ? 'bg-white/20 text-white'
                                                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover/item:text-indigo-600 dark:group-hover/item:text-indigo-300',
                                                                )}
                                                            >
                                                                {option.tag}
                                                            </span>
                                                        ) : null}
                                                        {isSelected ? (
                                                            <Check
                                                                size={14}
                                                                className="text-white shrink-0 stroke-[3] ml-1"
                                                            />
                                                        ) : (
                                                            <div className="w-3.5 h-3.5 ml-1" />
                                                        )}
                                                    </div>
                                                </button>
                                            </div>
                                        )
                                    })}
                                </div>
                            ) : (
                                /* Normal Direct Rendering for <= 12 Items */
                                <div className="space-y-1">
                                    {filteredOptions.map((option, idx) => {
                                        const isSelected =
                                            option.value === optimisticValue ||
                                            option.value === value
                                        const isHighlighted = idx === highlightedIndex
                                        const liveQuote = getLiveQuote?.(option.value)

                                        return (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onPointerDown={(e) => handleSelect(option.value, e)}
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                }}
                                                onMouseEnter={() => setHighlightedIndex(idx)}
                                                className={cn(
                                                    'w-full flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-all duration-75 text-left outline-none group/item',
                                                    isSelected
                                                        ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-md shadow-indigo-500/25'
                                                        : isHighlighted
                                                          ? 'bg-slate-100 dark:bg-slate-800/80 text-indigo-600 dark:text-indigo-300'
                                                          : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100/80 dark:hover:bg-slate-800/60',
                                                )}
                                            >
                                                <div className="flex items-center gap-2.5 min-w-0">
                                                    {option.badge && (
                                                        <span
                                                            className={cn(
                                                                'text-[9px] px-1.5 py-0.5 rounded-md font-black uppercase tracking-wider shrink-0 transition-colors',
                                                                isSelected
                                                                    ? 'bg-white/20 text-white border border-white/30'
                                                                    : getBadgeClasses(
                                                                          option.badgeColor,
                                                                      ),
                                                            )}
                                                        >
                                                            {option.badge}
                                                        </span>
                                                    )}
                                                    <div className="flex flex-col min-w-0">
                                                        <span
                                                            className={cn(
                                                                'text-xs font-black tracking-tight truncate',
                                                                isSelected
                                                                    ? 'text-white'
                                                                    : 'text-slate-800 dark:text-slate-100',
                                                            )}
                                                        >
                                                            {option.label}
                                                        </span>
                                                        {option.subtitle && (
                                                            <span
                                                                className={cn(
                                                                    'text-[10px] font-bold leading-tight truncate',
                                                                    isSelected
                                                                        ? 'text-indigo-100/80'
                                                                        : 'text-slate-400 dark:text-slate-500',
                                                                )}
                                                            >
                                                                {option.subtitle}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2 shrink-0 ml-3">
                                                    {liveQuote && liveQuote.ltp ? (
                                                        <div className="flex flex-col items-end">
                                                            <span
                                                                className={cn(
                                                                    'text-xs font-black font-mono tracking-tight tabular-nums',
                                                                    isSelected
                                                                        ? 'text-white'
                                                                        : 'text-slate-800 dark:text-slate-100',
                                                                )}
                                                            >
                                                                ₹
                                                                {liveQuote.ltp.toLocaleString('en-IN', {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2,
                                                                })}
                                                            </span>
                                                            {liveQuote.changePercent !==
                                                                undefined && (
                                                                <span
                                                                    className={cn(
                                                                        'text-[9px] font-bold font-mono tracking-tight tabular-nums flex items-center gap-0.5',
                                                                        isSelected
                                                                            ? 'text-white/90'
                                                                            : liveQuote.changePercent >=
                                                                              0
                                                                              ? 'text-emerald-600 dark:text-emerald-400'
                                                                              : 'text-rose-600 dark:text-rose-400',
                                                                    )}
                                                                >
                                                                    {liveQuote.changePercent >= 0
                                                                        ? '+'
                                                                        : ''}
                                                                    {liveQuote.changePercent.toFixed(2)}
                                                                    %
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : option.tag ? (
                                                        <span
                                                            className={cn(
                                                                'text-[10px] px-2 py-0.5 rounded-full font-bold font-mono tracking-tight tabular-nums',
                                                                isSelected
                                                                    ? 'bg-white/20 text-white'
                                                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover/item:text-indigo-600 dark:group-hover/item:text-indigo-300',
                                                            )}
                                                        >
                                                            {option.tag}
                                                        </span>
                                                    ) : null}
                                                    {isSelected ? (
                                                        <Check
                                                            size={14}
                                                            className="text-white shrink-0 stroke-[3] ml-1"
                                                        />
                                                    ) : (
                                                        <div className="w-3.5 h-3.5 ml-1" />
                                                    )}
                                                </div>
                                            </button>
                                        )
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Footer Keyboard Hints */}
                        <div className="flex items-center justify-between px-2 pt-2 mt-1 border-t border-slate-100 dark:border-slate-800/80 text-[10px] text-slate-400 dark:text-slate-500 font-medium">
                            <div className="flex items-center gap-1.5">
                                <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono text-[9px] font-bold">
                                    ↑↓
                                </span>
                                <span>navigate</span>
                                <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono text-[9px] font-bold ml-1">
                                    ↵
                                </span>
                                <span>select</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono text-[9px] font-bold">
                                    esc
                                </span>
                                <span>close</span>
                            </div>
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    )
}
